import { memo, useState } from "react";
import styled from "styled-components";
import { UI_Header, UI_HeaderLeft, UI_HeaderRight, UI_HeaderRight_item } from "../PanelStyle";
import { FaXmark } from "react-icons/fa6";
import { PiGearSixBold } from "react-icons/pi";
import { Rnd } from 'react-rnd';
import { useAtom } from 'jotai';
import FormItemWrapper from "~/components/common/FormItemWrapper/FormItemWrapper";
import { headerCenter_currentState_Atom, HeaderCenter_State } from "~/layouts/components/Header/HeaderCenter/store";
import { Tabs, Button, Switch, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import TextArea from "antd/es/input/TextArea";

const ProjectSettingPanel = () => {
  const { t } = useTranslation();
  const [currentState, setCurrentProject] = useAtom(headerCenter_currentState_Atom);

  // State for form fields
  const [description, setDescription] = useState<string>('');
  const [viewersCanOpenTable, setViewersCanOpenTable] = useState<boolean>(true);
  const [openTableByDefault, setOpenTableByDefault] = useState<string>('None');
  const [duplicateMap, setDuplicateMap] = useState<boolean>(true);
  const [seeMapPresence, setSeeMapPresence] = useState<boolean>(true);
  const [exportData, setExportData] = useState<boolean>(false);

  const handleDone = () => {
    setCurrentProject(HeaderCenter_State.NONE);
  };

  const tabItems = [
    {
      key: 'general',
      label: t('map_settings_tab_general'),
      children: (
        <UI_TabContent>
          {/* Description Section */}
          <UI_Section>
            <UI_SectionTitle>{t('map_settings_description_title')}</UI_SectionTitle>
            <TextArea
              placeholder={t('map_settings_description_placeholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              autoSize={{ minRows: 4, maxRows: 6 }}
              style={{
                borderRadius: '6px',
                border: '1px solid #d9d9d9'
              }}
            />
          </UI_Section>

          {/* Map constraints Section */}
          <UI_Section>
            <UI_SectionTitle>{t('map_settings_constraints_title')}</UI_SectionTitle>
            <UI_SectionDescription>
              {t('map_settings_constraints_description')}
            </UI_SectionDescription>
            <Button
              type="default"
              style={{
                borderRadius: '6px',
                height: '36px'
              }}
            >
              {t('map_settings_constraints_add_button')}
            </Button>
          </UI_Section>

          {/* Table settings Section */}
          <UI_Section>
            <UI_SectionTitle>{t('map_settings_table_title')}</UI_SectionTitle>

            <UI_SettingRow>
              <UI_SettingLabel>
                <span>{t('map_settings_table_viewers_can_open')}</span>
              </UI_SettingLabel>
              <Switch
                checked={viewersCanOpenTable}
                onChange={setViewersCanOpenTable}
              />
            </UI_SettingRow>

            <UI_SettingRow>
              <UI_SettingLabel>
                <span>{t('map_settings_table_open_by_default')}</span>
              </UI_SettingLabel>
              <Select
                value={openTableByDefault}
                onChange={setOpenTableByDefault}
                style={{ width: 120 }}
                options={[
                  { label: t('map_settings_table_none'), value: 'None' },
                  { label: t('map_settings_table_table1'), value: 'Table1' },
                  { label: t('map_settings_table_table2'), value: 'Table2' },
                ]}
              />
            </UI_SettingRow>
          </UI_Section>

          {/* Viewer permissions Section */}
          <UI_Section>
            <UI_SectionTitle>{t('map_settings_permissions_title')}</UI_SectionTitle>

            <UI_SettingRow>
              <UI_SettingLabel>
                <span>{t('map_settings_permissions_duplicate_map')}</span>
              </UI_SettingLabel>
              <Switch
                checked={duplicateMap}
                onChange={setDuplicateMap}
              />
            </UI_SettingRow>

            <UI_SettingRow>
              <UI_SettingLabel>
                <span>{t('map_settings_permissions_see_presence')}</span>
              </UI_SettingLabel>
              <Switch
                checked={seeMapPresence}
                onChange={setSeeMapPresence}
              />
            </UI_SettingRow>

            <UI_SettingRow>
              <UI_SettingLabel>
                <span>{t('map_settings_permissions_export_data')}</span>
                <Button
                  type="link"
                  size="small"
                  style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#1890ff',
                    padding: '0',
                    height: 'auto'
                  }}
                >
                  {t('map_settings_permissions_upgrade')}
                </Button>
              </UI_SettingLabel>
              <Switch
                checked={exportData}
                onChange={setExportData}
                disabled
              />
            </UI_SettingRow>
          </UI_Section>
        </UI_TabContent>
      ),
    },
    {
      key: 'developers',
      label: t('map_settings_tab_developers'),
      children: (
        <UI_TabContent>
          <p>{t('map_settings_developers_coming_soon')}</p>
        </UI_TabContent>
      ),
    },
  ];

  return (
    <Rnd
      default={{
        x: Math.max(10, window.innerWidth - 440),
        y: 60,
        width: 420,
        height: 600,
      }}
      minWidth={340}
      minHeight={400}
      bounds="window"
      dragHandleClassName="draggable-header"
      enableResizing={{
        bottom: true,
        right: true,
        bottomRight: true,
        left: true,
        bottomLeft: true,
        top: false,
        topLeft: false,
        topRight: false,
      }}
      style={{ zIndex: 1000 }}
    >
      <UI_Wrapper>
        {/* Header */}
        <UI_Header className="draggable-header" style={{ cursor: 'move', userSelect: 'none' }}>
          <UI_HeaderLeft style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PiGearSixBold style={{ color: '#2563eb', fontSize: '18px' }} />
            <span>{t('map_settings_title')}</span>
          </UI_HeaderLeft>
          <UI_HeaderRight>
            <UI_HeaderRight_item onClick={handleDone}>
              <FaXmark />
            </UI_HeaderRight_item>
          </UI_HeaderRight>
        </UI_Header>
        {/* End Header */}

        {/* Content */}
        <UI_Content>
          <Tabs
            items={tabItems}
            defaultActiveKey="general"
            style={{
              height: '100%'
            }}
          />
        </UI_Content>

        {/* Footer with Done button */}
        <UI_Footer>
          <Button
            type="primary"
            onClick={handleDone}
            style={{
              borderRadius: '6px',
              fontWeight: '500',
              height: '36px',
              padding: '0 24px',
              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.25)',
            }}
          >
            {t('map_settings_done_button')}
          </Button>
        </UI_Footer>
        {/* End Content */}
      </UI_Wrapper>
    </Rnd>
  );
}

export default memo(ProjectSettingPanel);

const UI_Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(226, 232, 240, 0.85);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const UI_Content = styled.div`
  flex: 1;
  padding: 0;
  overflow: hidden;

  .ant-tabs-nav {
    .ant-tabs-tab {
      padding-left: 16px;
      padding-right: 16px;
    }
  }
`;

const UI_TabContent = styled.div`
  padding: 16px;
  height: 100%;
  overflow-y: auto;
`;

const UI_Section = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const UI_SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #262626;
  margin-bottom: 8px;
`;

const UI_SectionDescription = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 12px;
  line-height: 1.5;
`;

const UI_SettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const UI_SettingLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;

  span {
    font-size: 14px;
    color: #262626;
  }
`;

const UI_Footer = styled.div`
  border-top: 1px solid #f0f0f0;
  padding: 16px;
  text-align: right;
  background-color: white;
  border-radius: 0 0 8px 8px;
`;

const UI_List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
